import { Component, Input, OnInit } from '@angular/core';

import { StateService } from '@uirouter/angular';
import { GfTranslateService } from '../../../../services/core.translate.service';
import { FilterService } from '../../../../services/core.filter.service';
import { UiService } from '../../../../services/core.ui.service';
import { ScmReportHelper } from './project.report.scm.service.component';
import { ProjectService } from '../../../../services/project.service';
import { ProjectReportsData } from '../../services/project.reports.data.service';
import { DateGfUtcDatePipe } from '../../../../pipes/date/date.gf-utc-date.pipe';

import * as _ from 'lodash';
import * as d3 from 'd3';

@Component({
    selector: 'project-scm-report',
    template: require('./project.report.scm.component.html')
})
export class ProjectScmReportComponent implements OnInit {
    sortFields: any;

    @Input() project: any = null;

    private baseFilters: any = false;
    private paging: any = false;
    private sort: any = false;
    protected filters: any = false;
    protected charts: any = false;
    protected showDetails = false;
    protected commits: any = false;
    protected flatCommits: any = false;
    protected dateChartOptions: any = null;
    protected committersChartOptions: any = null;

    constructor(
        private stateService: StateService,
        private gfTranslateService: GfTranslateService,
        private filterService: FilterService,
        private uiService: UiService,
        private scmReportHelper: ScmReportHelper,
        private projectService: ProjectService,
        private projectReportsData: ProjectReportsData,
    ) { }
    
    ngOnInit() {
        this.dateChartOptions = {
            chart: {
                type: "multiBarChart",
                height: 350,
                margin: {
                    top: 20,
                    right: 20,
                    bottom: 60,
                    left: 40
                },
                yAxis: {
                    tickFormat: d3.format(',f')
                },
                useInteractiveGuideline: false,
                showLegend: false,
                showControls: false,
                noData: this.gfTranslateService.translate("No Data Available"),
                color: ['#99CC33'],
                reduceXTicks: false,
                rotateLabels: 45
            }
        };

        this.committersChartOptions = {
            chart: {
                type: 'pieChart',
                height: 350,
                noData: this.gfTranslateService.translate("No Data Available"),
                x: (d) => { return d.key; },
                y: (d) => { return d.y; },
                legendPosition: 'bottom',
                valueFormat: d3.format(".0f"),
                showLabels: false,
                transitionDuration: 200,
                labelThreshold: 0.01,
                showLegend: true,
                tooltips: true,
                labelsOutside: true
            }
        };

        this.paging = this.filterService.getPagingValues();
        this.sort = this.filterService.getSortValues();
        this.baseFilters = this.scmReportHelper.getBaseFilters();

        this.filters = {
            main_fields: this.scmReportHelper.setFiltersFromStateParams(
                _.cloneDeep(this.baseFilters)
            )
        };

        this.refreshData();
    };

    onApplyFilters = () => {
        this.paging.page_num = 1;
        this.charts = false;
        this.commits = false;
        this.applyFilters();
    };

    onResetFilters = () => {
        this.paging.page_num = 1;
        this.filters = {
            main_fields: _.cloneDeep(this.baseFilters)
        };

        this.applyFilters();
    };

    setSort = (params) => {
        this.sort = {
            sort_field: _.get(params, 'key', 'id'),
            sort_dir: _.get(params, 'sortDir', 'asc')
        };
        this.applyFilters();
    };

    downloadData(format) {
        this.projectReportsData.runReport(this.getParams(), format);
    };

    onPaging = (params) => {
        var newParams = this.getParams(params);
        var opts = { reload: false, location: true, notify: false };
        this.stateService.go('.', newParams, opts).then(this.loadCommitDetails, this.loadCommitDetails);
    };

    refreshData() {
        this.charts = false;
        this.showDetails = this.getShowDetails();

        var params = this.getParams();

        // For the top half of the report, always use
        // the no-details version of the API call.
        params.details = 'N';

        this.projectService.report(params)
            .then(
                (response) => {
                    var items = _.get(response, 'items', []);

                    // We need this for two purposes in this code block,
                    // so we can't just stack getCommitChartData inside 
                    // getDateChartData, below.
                    var commitChartData = this.scmReportHelper.getCommitChartData(
                        _.get(items, 'commits', [])
                    );

                    this.charts = {
                        dateChartData: this.scmReportHelper.getDateChartData(commitChartData),
                        committersChartData: this.projectReportsData.setPieChartData(
                            _.get(items, 'committers', [])
                        )
                    };

                    this.dateChartOptions.chart.reduceXTicks = this.scmReportHelper.getReduceXTicks(commitChartData);
                },
                () => {
                    this.charts = {
                        dateChartData: [],
                        committersChartData: []
                    };
                    var msg = this.gfTranslateService.translate('There was a problem loading scm commits data');
                    this.uiService.message.showWarning(msg, 'Data Error');
                }
            ).finally(() => {
                if (this.showDetails) {
                    this.loadCommitDetails();
                }
            });
    };

    loadCommitDetails() {
        this.paging = this.filterService.getPagingValues();

        const gfDateFn = new DateGfUtcDatePipe();

        var params = this.getParams();
        // If we're showing the commits table,
        // make the with-details version of the API call.
        params.details = 'Y';

        this.projectService.report(params)
            .then(
                (response) => {
                    this.flatCommits = _.sortBy(_.get(response, 'items', []), 'date');
                    this.commits = _.reduce(
                        _.groupBy(this.flatCommits, (c) => { return gfDateFn.transform(c.date, "YYYY-MM-DD"); }),
                        (carry, item, key) => {
                            carry.push({
                                key: key,
                                items: item
                            });
                            return carry;
                        },
                        []
                    );
                },
                () => {
                    this.commits = [];
                    var msg = this.gfTranslateService.translate('There was an issue loading scm commits data', this.stateService.params);
                    this.uiService.message.showWarning(msg, 'Data Error');
                }
            );
    }

    getShowDetails() {
        return _.get(this.filters, 'main_fields[1].value.id', 'N') == 'Y';
    }

    getParams(overrides = {}) {

        return Object.assign(
            { project: this.stateService.params.unixName, type: this.stateService.params.type },
            this.sort,
            this.paging,
            this.filterService.getAllFilterValues(
                _.get(this.filters, 'main_fields', [])
            ),
            overrides || {}
        );
    }

    applyFilters(overrides = {}) {
        var params = this.getParams(overrides);

        var opts = { reload: false, location: true, notify: false };
        this.stateService.go('.', params, opts).then(
            () => {
                this.refreshData();
            }
        );
    }
}
